import React, { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { ToolCall } from "@/types";
import { formatToolDisplayName } from "@/utils/toolChevrons";
import WorkingIndicator from "./WorkingIndicator";

interface ToolChevronProps {
  /** Group of tool calls for one cluster of agent activity. */
  calls: ToolCall[];
}

/**
 * A collapsible summary of one cluster of agent tool calls, rendered inline
 * between assistant text bubbles (`thinkingChevron` feature).
 *
 * Visual hierarchy: tool activity is *secondary* to the conversation — it
 * explains how an answer was produced, not the answer itself. Chat bubbles are
 * filled cards (`bg-gray-50` / `bg-[#0F141A]`, no border); these rows are
 * deliberately chromeless — no border, no fill until hover, muted small text —
 * so they read as quiet metadata between bubbles rather than competing with
 * them. An earlier version had a border and `font-medium` foreground text,
 * which made the metadata look heavier than the content it described.
 *
 * Collapsed by default and stays collapsed after completion. While any call is
 * running the row shows a spinner plus a cycling working indicator, so ongoing
 * work is visible without expanding. Tool detail can be arbitrarily long (a
 * verbose search query, a deep path), so labels truncate rather than widening
 * the row — an unbounded label gives the whole chat panel a horizontal
 * scrollbar.
 */
const ToolChevron: React.FC<ToolChevronProps> = ({ calls }) => {
  const [expanded, setExpanded] = useState(false);

  if (calls.length === 0) return null;

  const isRunning = calls.some((call) => call.status === "running");
  const hasError = calls.some((call) => call.status === "error");
  const runningCall = calls.find((call) => call.status === "running");

  const totalCalls = calls.reduce(
    (sum, call) => sum + (call.repeatCount ?? 1),
    0,
  );
  const isSingle = calls.length === 1;

  // A single entry names its tool and shows its detail inline; a mixed cluster
  // summarizes as a count and defers detail to the expanded view.
  const headerLabel = isSingle
    ? formatToolDisplayName(calls[0].toolName)
    : `${totalCalls} tool ${totalCalls === 1 ? "call" : "calls"}`;
  // Only when collapsed: once expanded, the row below carries the detail, and
  // showing it in both places is redundant.
  const headerDetail =
    isSingle && !isRunning && !expanded ? calls[0].details : undefined;

  return (
    <div className="rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="flex w-full min-w-0 items-center gap-1.5 px-2 py-1.5 text-left rounded-lg hover:bg-muted/50 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground/70 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground/70 flex-shrink-0" />
        )}

        {/* Status. A spinner while running so the row always reads as active,
            even between calls in the same cluster. */}
        {isRunning ? (
          <Loader2
            className="h-3 w-3 animate-spin text-blue-600/80 dark:text-blue-400/80 flex-shrink-0"
            aria-label="Working"
          />
        ) : hasError ? (
          <AlertCircle className="h-3 w-3 text-amber-600/80 dark:text-amber-400/80 flex-shrink-0" />
        ) : (
          <CheckCircle2 className="h-3 w-3 text-green-600/70 dark:text-green-400/70 flex-shrink-0" />
        )}

        {/* Text stays at full `muted-foreground` — stacking opacity on an
            already-muted color pushed it below comfortable reading contrast.
            The rows read as secondary through size and the absent border/fill,
            not by fading the words. */}
        <span className="text-xs text-muted-foreground flex-shrink-0">
          {headerLabel}
          {isSingle && (calls[0].repeatCount ?? 1) > 1 && (
            <span className="ml-1 text-muted-foreground/80">
              ×{calls[0].repeatCount}
            </span>
          )}
        </span>

        {/* Completed single call: show its detail inline so the useful part
            ("Creating adoption_plan.md") is visible without expanding. */}
        {headerDetail && (
          <span
            className="text-xs text-muted-foreground min-w-0 flex-1 truncate"
            title={headerDetail}
          >
            {headerDetail}
          </span>
        )}

        {/* Live activity without expanding. A <span> because this sits inside a
            <button>, which can't contain block-level content; `compact` makes
            WorkingIndicator render spans too. `flex` so the indicator's own row
            participates in this row's `items-center` alignment — otherwise it
            sits slightly above the tool name. */}
        {isRunning && (
          <span className="ml-auto flex min-w-0 flex-1 items-center">
            <WorkingIndicator
              compact
              alternateText={runningCall?.details}
              alternateInterval={3000}
            />
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-2 pb-1.5 pl-7 space-y-0.5">
          {calls.map((call, index) => (
            <div
              key={`${call.toolUseId ?? call.toolName}-${index}`}
              className="flex items-start gap-2 min-w-0"
            >
              {/* break-words (not truncate) so the full detail stays readable
                  once the user has opted in by expanding. Full opacity here in
                  particular: this is the content the user opened the row to
                  read, so it shouldn't be the faintest text on screen. */}
              <span className="text-xs text-muted-foreground min-w-0 break-words">
                {formatToolDisplayName(call.toolName)}
                {(call.repeatCount ?? 1) > 1 && (
                  <span className="ml-1">×{call.repeatCount}</span>
                )}
                {call.details && (
                  <span className="ml-1.5">— {call.details}</span>
                )}
              </span>
              {call.status === "running" && (
                <Loader2 className="h-3 w-3 mt-0.5 animate-spin text-blue-600/80 dark:text-blue-400/80 flex-shrink-0" />
              )}
              {call.status === "error" && (
                <span className="text-xs text-amber-600/80 dark:text-amber-400/80 flex-shrink-0">
                  failed
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ToolChevron;
